import React from 'react';
import { Box, Collapse, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import useToggle from '../../../components/Cabeceras/hooks/useToggle';
import {
    CUSTOM_HABILITATION_TITLE_OPTION,
    isExisitingHabilitationTitle
} from '../../../commons/presentations';

interface ResponsableCargaSectionProps {
    responsables: any[];
}

const ResponsableCargaSection: React.FC<ResponsableCargaSectionProps> = ({ responsables }) => {
    const { t } = useTranslation('presentacionesRar');
    const [isExpanded, toggleExpanded] = useToggle(true);

    const responsablesR = responsables?.filter((r: any) => r?.cargo?.codigo === 'R');

    if (!responsablesR || responsablesR.length === 0) return null;

    const renderField = (label: string, value: React.ReactNode) => (
        <>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1E272E' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#3f4b59' }}>{value}</Typography>
        </>
    );

    return (
        <Box sx={{ background: 'white', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h3" sx={{ fontWeight: '600', color: '#1E272E' }}>
                    {t('responsableData.title')}
                </Typography>
                <IconButton
                    onClick={toggleExpanded}
                    sx={{
                        width: 22,
                        height: 22,
                        ml: 1.5,
                        mt: -1.5,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.16)' }
                    }}
                >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>
            <Collapse in={isExpanded}>
                {responsablesR.map((responsable: any, index: number) => (
                    <Grid container spacing={2} sx={{ pb: 4 }} key={index}>
                        <Grid item xs={12} sm={6} md={3} lg={1.5}>
                            {renderField(
                                t('responsableData.name'),
                                `${responsable.nombre || ''} ${responsable.apellido || ''}`.trim()
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.5}>
                            {renderField(t('responsableData.CUIT'), responsable.cuit || 'N/A')}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.5}>
                            {renderField(
                                t('responsableData.cargo'),
                                responsable.cargo?.nombre || 'N/A'
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.5}>
                            {renderField(
                                t('responsableData.status'),
                                responsable.tipoContratacion?.nombre ||
                                    responsable.tipoContratacion ||
                                    'N/A'
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.5}>
                            {renderField(
                                t('responsableData.tituloHabilitante'),
                                isExisitingHabilitationTitle(responsable?.titulo) ? (
                                    responsable?.titulo
                                ) : (
                                    <span>
                                        {responsable?.titulo
                                            ? CUSTOM_HABILITATION_TITLE_OPTION
                                            : 'N/A'}
                                        {responsable?.titulo && (
                                            <Tooltip
                                                title={responsable?.titulo || 'Missing information'}
                                            >
                                                <span
                                                    style={{
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer'
                                                    }}
                                                ></span>
                                            </Tooltip>
                                        )}
                                    </span>
                                )
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.5}>
                            {renderField(
                                t('responsableData.matriculaNumber'),
                                responsable.matricula || 'N/A'
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1.5}>
                            {renderField(
                                t('responsableData.entidadOtorgante'),
                                responsable.entidadOtorgante || 'N/A'
                            )}
                        </Grid>
                    </Grid>
                ))}
            </Collapse>
        </Box>
    );
};

export default ResponsableCargaSection;
